/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Button,
  Paper,
} from '@mui/material';
import { fDateTime } from 'utils/formatTime';
import { fNumber } from 'utils/formatNumber';
import useIsMountedRef from 'hooks/useIsMountedRef';
import axios, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';

ReferralHistory.propTypes = {
  user: PropTypes.object.isRequired,
};

export default function ReferralHistory({ user }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  // Sorting and pagination state
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0); // zero-based page index
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Data state
  const [data, setData] = useState({
    data: [],
    current_page: 1,
    total_pages: 0,
    total_size: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData(page + 1, rowsPerPage, sortOrder);
    // eslint-disable-next-line
  }, [page, rowsPerPage, sortOrder]);

  async function fetchData(pageNum, perPage, order) {
    setLoading(true);
    setError(null);
    try {
      const queryStr = `?sort_order=${order}&page=${pageNum}&user_id=${user.user_id}&page_size=${perPage}`;
      const response = await axios.get(CRUD.user.referralList + queryStr);
      const ret = response.data;
      if (isMountedRef.current) {
        // Add ids if missing for keys
        const arrayWithIds = Array.isArray(ret.data)
          ? ret.data.map((item, idx) => ({
              ...item,
              id: item.id || item._id || item.user_id || `row-${idx + 1}`,
            }))
          : ret.data;
        setData({
          data: arrayWithIds,
          current_page: ret.current_page || pageNum,
          total_pages: ret.total_pages || 0,
          total_size: ret.total_size || 0,
        });
      }
    } catch (err) {
      setError(getErrorMessage(err));
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }
  function handleChangeRowsPerPage(event) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  // Sorting handler - toggles asc/desc for now on Name column
  function toggleSortOrder() {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(0);
  }

  return (
    <Card sx={{ p: 3, mt: 2, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight={700}>
          Referral History
        </Typography>

        {/* Controls area */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ userSelect: 'none' }}>
              Sort Order:
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={toggleSortOrder}
              sx={{ textTransform: 'capitalize' }}
            >
              {sortOrder === 'asc' ? 'Ascending ↑' : 'Descending ↓'}
            </Button>
          </Stack>

          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={() => fetchData(page + 1, rowsPerPage, sortOrder)}
            disableElevation
            size="medium"
          >
            Apply Changes
          </LoadingButton>
        </Stack>

        {/* Error message */}
        {error && (
          <Typography color="error" variant="body2" sx={{ my: 1 }}>
            {error}
          </Typography>
        )}

        {/* Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader size={isMobile ? 'small' : 'medium'} aria-label="referral history table">
            <TableHead>
              <TableRow>
                <TableCell width={260} sx={{ fontWeight: 700 }}>
                  User
                </TableCell>
                {/* <TableCell width={200} sx={{ fontWeight: 700 }}>
                  Email
                </TableCell> */}
                {/* <TableCell width={140} sx={{ fontWeight: 700 }}>
                  Phone
                </TableCell> */}
                <TableCell width={180} sx={{ fontWeight: 700 }}>
                  Joined At
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {!loading && data.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    No referral data found.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                data.data.map((row) => (
                  <TableRow key={row.id} hover tabIndex={-1}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          alt={`${row.first_name} ${row.last_name}`}
                          src={row.image_url}
                          sx={{ width: 44, height: 44 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={700} noWrap>
                            {row.first_name} {row.last_name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                            <Chip
                              size="small"
                              label={`ID: ${row.user_id}`}
                              sx={{ bgcolor: 'grey.100', fontWeight: 600, fontSize: 12 }}
                            />
                            {row.phone && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {row.phone}
                              </Typography>
                            )}
                          </Stack>
                          {row.email && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                              {row.email}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    {/* <TableCell>{row.email} <br/> {row.phone}</TableCell> */}
                    {/* <TableCell></TableCell> */}
                    <TableCell>{fDateTime(new Date(row.created_at))}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={data.total_size}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page"
          sx={{ mt: 2 }}
        />
      </Stack>
    </Card>
  );
}
